import { useState, type ReactNode } from "react";
import { styled } from "@pigment-css/react";
import {
  Card,
  Header,
  Headline,
  HeadlineSmall,
  KpiHeadline,
  MetaLabel,
} from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { TrendBarChart } from "@acto/taskpane/field-sales-2/components/primitives/TrendBarChart";
import { TrendComparison } from "@acto/taskpane/field-sales-2/components/primitives/TrendComparison";
import { useHasFinePointer } from "@acto/taskpane/field-sales-2/utils/hooks/useHasFinePointer";
import type { KpiTrend } from "@acto/taskpane/field-sales-2/data-access/useKpiTrend";

const STALE_OPACITY = 0.5;

const DimmingOverlay = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[24],
  transition: "opacity 150ms ease",
}));

export interface CustomerRevenueCardProps {
  /** Optional content above the identity header, separated from it by a divider. */
  headerSlot?: ReactNode;
  /** Small title above the name (the customer's external reference). Omitted when the
   *  card is titled by a drilled product group, which has no reference to show. */
  externalReference?: string;
  /** Card title: the customer name, or the drilled product group's name. */
  name: string;
  /** Title type style: "default", or the more compact "small". */
  titleVariant?: "default" | "small";
  /** KPI trend view-model from {@link useKpiTrend}. */
  trend: KpiTrend;
  /** Optional content rendered inside the card, below the chart (e.g. a details button). */
  footer?: ReactNode;
}

/**
 * The customer revenue-trend card: identity, current-period total, the
 * year-over-year comparison, the bar chart, and an optional CTA. Renders nothing
 * once the query resolves with no data. Shared by the overview and analysis pages so
 * their card stays identical.
 *
 * Tap-to-focus is gated to coarse pointers: a fine pointer keeps the chart's hover tooltip, which
 * cannot coexist with tap selection.
 */
export function CustomerRevenueCard({
  headerSlot,
  externalReference,
  name,
  titleVariant = "default",
  trend,
  footer,
}: CustomerRevenueCardProps) {
  const [selection, setSelection] = useState<{ key: string; index: number } | null>(null);
  const isCoarsePointer = !useHasFinePointer();

  // A drill, another customer or a new range invalidates the focused index, so the selection carries
  // the key it was made under and stops matching when one changes. A fine pointer gates it off.
  const focusKey = `${externalReference ?? ""}|${name}|${trend.chart.periodStarts.join("|")}`;
  const selectedBar = isCoarsePointer && selection?.key === focusKey ? selection.index : null;

  if (trend.isEmpty) return null;

  const Title = titleVariant === "small" ? HeadlineSmall : Headline;

  const focused = selectedBar !== null ? trend.buckets[selectedBar] : undefined;
  const comparison = focused ? focused.comparison : trend.comparison;

  return (
    <Card>
      {headerSlot && (
        <>
          {headerSlot}
          <Divider />
        </>
      )}
      <Header>
        {externalReference && <MetaLabel>{externalReference}</MetaLabel>}
        <Title>{name}</Title>
      </Header>
      <Divider />
      <DimmingOverlay
        aria-busy={trend.isPending}
        style={{
          opacity: trend.isPending ? STALE_OPACITY : 1,
          pointerEvents: trend.isPending ? "none" : undefined,
        }}
      >
        <Header>
          <MetaLabel>{focused?.label ?? trend.rangeLabel}</MetaLabel>
          <KpiHeadline>{focused?.total ?? trend.total}</KpiHeadline>
        </Header>
        {comparison && <TrendComparison {...comparison} />}
        <TrendBarChart
          {...trend.chart}
          selectedIndex={selectedBar}
          onSelect={
            isCoarsePointer
              ? (index) => setSelection(selectedBar === index ? null : { key: focusKey, index })
              : undefined
          }
        />
      </DimmingOverlay>
      {footer && (
        <>
          <Divider />
          {footer}
        </>
      )}
    </Card>
  );
}
